import type { Ref } from 'react';
import React, { useCallback, useState } from 'react';
import type { BasePanelProps, ValidIconName } from '../basic';
import { Panel, Tool } from '../basic';
import type { SplitterLayoutProps } from 'react-splitter-layout';
import SplitterLayout from 'react-splitter-layout';
import { isEmptyString } from '../../util';
import type {
  ClassNameProp,
  DivElementProps,
  StateSetter
} from '../common-types';
import { intl } from '../../lib';

/**
 * Config for the panels in the SplitView.
 * This is for what can be passed to the {@link SplitPanel}s from the outside.
 */
interface SplitViewPanelConfig
  extends Omit<BasePanelProps, 'children' | 'collapsible'> {
  /**
   * If the panel should be highlighted.
   * The panel needs a title for the highlighting to take effect.
   */
  highlighted?: boolean;
  /**
   * Panel content.
   */
  render: () => React.JSX.Element;
  /**
   * SplitPanel ref.
   */
  ref?: Ref<HTMLDivElement>;
  /**
   * If the expanded state needs to be controlled from the outside.
   */
  expanded?: boolean;
  /**
   * State setter for the expanded state controlled from the outside.
   */
  setExpanded?: StateSetter<boolean>;
}

/**
 * Props for a {@link SplitPanel} component.
 */
interface SplitPanelProps extends SplitViewPanelConfig {
  /**
   * If primary or secondary.
   */
  primary: boolean;
}

/**
 * Panels used inside a {@link SplitView} component.
 *
 * @param properties a {@link SplitPanelProps} object
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
export const SplitPanel = function SplitPanel({
  highlighted = false,
  render,
  tools,
  className = '',
  primary,
  title,
  ref,
  ...props
}: Readonly<SplitPanelProps> & {
  ref?: Ref<HTMLDivElement>;
}): React.JSX.Element {
  // build the panel css
  const panelClasses = isEmptyString(className) ? [] : [className.trim()];
  panelClasses.push('ehs-splitview-panel');
  if (primary) {
    panelClasses.push('primary');
  } else {
    panelClasses.push('secondary');
  }

  // highlighted only if also a title is set
  if (highlighted && title) {
    panelClasses.push('highlighted');
  }

  // all the panelClasses together as string
  const panelClassname = panelClasses.join(' ');

  return (
    <Panel
      ref={ref}
      tools={tools}
      className={panelClassname}
      title={title}
      {...props}
    >
      {render()}
    </Panel>
  );
};

/**
 * Props for a {@link SplitView} component.
 */
export interface SplitViewProps
  extends ClassNameProp,
    Pick<DivElementProps, 'style'> {
  /**
   * The {@link SplitterLayout} properties.
   */
  layout?: SplitterLayoutProps;
  /**
   * Properties for the left panel.
   */
  left: SplitViewPanelConfig;
  /**
   * Properties for the right panel.
   */
  right: SplitViewPanelConfig;
  /**
   * Tools to be shown for each panel.
   */
  tools?: BasePanelProps['tools'];
  /**
   * Put expand tool in front of custom tools.<br>
   * Default: false, expand tool goes after custom tools.
   */
  expandToolFirst?: boolean;
  /**
   * If a panel can be expanded or not.
   * This will automatically add a tool to all the panels,
   * which handles the expanded functionality.
   */
  allowExpanded?: boolean;
  /**
   * If only the primary pane is shown.
   */
  primaryOnly?: boolean;
  /**
   * If only the secondary pane is shown.
   */
  secondaryOnly?: boolean;
}

/**
 * This component is used to layout two panels horizontally or vertically.
 * The content of the panels is passed in the render function of their respective property
 * (left / right).
 *
 * Setting `primaryOnly` and `secondaryOnly` to true at the same time is not allowed.
 * When hiding this view dynamically using something like the 'hidden' css class, make sure to set
 * `format.secondaryInitialSize` to the correct value. Otherwise, the initial size will most likely be incorrect.
 *
 * This component uses [`react-splitter-layout`](https://github.com/zesik/react-splitter-layout) internally.
 *
 * @param properties a {@link SplitViewProps} object.
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
// overload for primaryOnly
export function SplitView(
  properties: SplitViewProps & {
    primaryOnly: boolean;
    secondaryOnly?: false;
  }
): React.JSX.Element;

// overload for secondaryOnly set
export function SplitView(
  properties: SplitViewProps & {
    primaryOnly?: false;
    secondaryOnly: boolean;
  }
): React.JSX.Element;

// overload to allow setting neither
export function SplitView(
  properties: SplitViewProps & {
    primaryOnly?: false;
    secondaryOnly?: false;
  }
): React.JSX.Element;

// implementation
export function SplitView({
  layout,
  left,
  right,
  tools = [],
  className = '',
  style,
  expandToolFirst = false,
  allowExpanded = true,
  primaryOnly = false,
  secondaryOnly = false,
  ...props
}: Readonly<SplitViewProps>): React.JSX.Element {
  if (primaryOnly && secondaryOnly) {
    throw new Error(
      'Setting both primaryOnly and secondaryOnly to true is not allowed. Please decide which one to show.'
    );
  }

  // local state to handle the expanded functionality
  const [primExpandedInternal, setPrimExpandedInternal] = useState(false);
  const [secExpandedInternal, setSecExpandedInternal] = useState(false);

  // to determine which panel is the 'primary' one
  const leftPrimary = layout?.primaryIndex !== 1;

  /**
   * Callback to determine which state is used. This is to help assign the correct
   * state value and state setter for a given state, depending on if the state is controlled or not.
   *
   * @param first if the `firstProp` should be considered the controlled state or not
   * @param firstProp property to be considered the controlled state, when `first` is true
   * @param secondProp property to be considered the controlled state, when `first` is false
   * @param internalState internal state to default to when not controlled
   * @return an Array of 2 items: The first is the correct state value, the second is the correct StateSetter
   */
  const getCorrectState = useCallback(
    (
      first: boolean,
      firstProp: typeof left,
      secondProp: typeof left,
      internalState: {
        state: typeof primExpandedInternal;
        setState: typeof setPrimExpandedInternal;
      }
    ): [boolean, typeof setPrimExpandedInternal] => {
      // state, defaulted to the internalState
      let elExpanded = internalState.state;
      let setElExpanded = internalState.setState;

      if (
        // when firstProp is controlled and first is true
        first &&
        firstProp.expanded !== undefined &&
        !!firstProp.setExpanded
      ) {
        elExpanded = firstProp.expanded;
        setElExpanded = firstProp.setExpanded;
      } else if (
        // when secondProp is controlled and first is false
        !first &&
        secondProp.expanded !== undefined &&
        !!secondProp.setExpanded
      ) {
        elExpanded = secondProp.expanded;
        setElExpanded = secondProp.setExpanded;
      }

      return [elExpanded, setElExpanded];
    },
    []
  );

  // get the state that is used by the component
  // this helps to automatically use the correct state depending on the situation
  // (controlled or not controlled)
  const [primExpanded, setPrimExpanded] = getCorrectState(
    leftPrimary,
    left,
    right,
    {
      state: primExpandedInternal,
      setState: setPrimExpandedInternal
    }
  );
  const [secExpanded, setSecExpanded] = getCorrectState(
    leftPrimary,
    right,
    left,
    {
      state: secExpandedInternal,
      setState: setSecExpandedInternal
    }
  );

  // helper to create the expand tool for the panels
  const getExpandTool = (primary: boolean): React.ReactNode => {
    if (!allowExpanded) {
      return <></>;
    }

    // switch between expand and collapse tooltip and icon
    const tooltip =
      primExpanded || secExpanded
        ? intl('sharedicomviewerwrapperportlet.tooltip.button.leavefullscreen')
        : intl('sharedicomviewerwrapperportlet.tooltip.button.enterfullscreen');
    const icon: ValidIconName =
      primExpanded || secExpanded ? 'exit-fullscreen' : 'fullscreen';

    return (
      <Tool
        icon={icon}
        key="expanded-0"
        tooltip={tooltip}
        onClick={() => {
          if (primary) {
            setPrimExpanded(!primExpanded);
          } else {
            setSecExpanded(!secExpanded);
          }
        }}
      />
    );
  };

  // css classes of the wrapper
  const wrapperDivClasses = ['ehs-split-view'];
  if (!isEmptyString(className)) wrapperDivClasses.push(className);
  if (allowExpanded && (primExpanded || secExpanded)) {
    wrapperDivClasses.push('expanded');
    if (primExpanded) {
      wrapperDivClasses.push('primary-expanded');
    } else if (secExpanded) {
      wrapperDivClasses.push('secondary-expanded');
    }
  }
  if (primaryOnly) wrapperDivClasses.push('primary-only');
  if (secondaryOnly) wrapperDivClasses.push('secondary-only');

  return (
    <div {...props} className={wrapperDivClasses.join(' ')} style={style}>
      <SplitterLayout {...layout}>
        <SplitPanel
          {...left}
          tools={[
            expandToolFirst && getExpandTool(leftPrimary),
            ...React.Children.toArray(left.tools),
            ...React.Children.toArray(tools),
            !expandToolFirst && getExpandTool(leftPrimary)
          ].filter(Boolean)}
          primary={leftPrimary}
        />
        <SplitPanel
          {...right}
          tools={[
            expandToolFirst && getExpandTool(!leftPrimary),
            ...React.Children.toArray(right.tools),
            ...React.Children.toArray(tools),
            !expandToolFirst && getExpandTool(!leftPrimary)
          ].filter(Boolean)}
          primary={!leftPrimary}
        />
      </SplitterLayout>
    </div>
  );
}
